import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { isAllowed } from "../../api/access/decisions";
import { chosenProjectDecision, useProjects } from "../../projects/projectContext";
import ProjectNeeded from "../../projects/ProjectNeeded";
import { usePaneSidebar } from "../../shell/workspace";
import StatusMessage from "../../ui/feedback/StatusMessage";
import { useCorpusSelection } from "./corpus/corpusSelectionContext";
import SourceConnections from "./connections/SourceConnections";
import { useImportPreparation } from "./import/useImportPreparation";
import AppButton from "../../ui/buttons/AppButton";
import ConnectorSetupButton from "../connectors/setup/ConnectorSetupButton";
import ImportProposalList from "../importProposals/ImportProposalList";
import ProjectBatches from "../batches/ProjectBatches";

/**
 * `/documents` with no document open: says what to do next (UX-3) and lists the connected
 * sources, the open imports and the project's batch runs, which live with the documents
 * (F108). `/documents/import` is the same page with the import's preparation opened beside
 * it (web-app.md WA-12).
 */
export default function SourcesIndexPage({ importing = false }: { importing?: boolean }) {
  const { t } = useTranslation();
  const projects = useProjects();
  const { corpora, selectedCorpus } = useCorpusSelection();
  const mayImport = isAllowed(chosenProjectDecision(projects, "propose_change"));
  const sidebar = usePaneSidebar();
  const startImport = useImportPreparation();
  // Once per visit of the address, as soon as the project allows it.
  const importOpened = useRef(false);
  useEffect(() => {
    if (!importing) {
      importOpened.current = false;
    } else if (startImport !== null && !importOpened.current) {
      importOpened.current = true;
      startImport();
    }
  }, [importing, startImport]);

  let nextStep;
  if (projects.selectedProjectId === null) {
    nextStep = <ProjectNeeded />;
  } else if (corpora?.status === "loaded" && selectedCorpus === null) {
    nextStep = (
      <StatusMessage tone="info" title={t("sources.index.noCorpusTitle")}>
        {startImport !== null ? (
          <AppButton variant="primary" size="sm" onClick={startImport}>{t("sources.index.noCorpusAction")}</AppButton>
        ) : (
          <p>{t("sources.index.noCorpusAskSomeone")}</p>
        )}
      </StatusMessage>
    );
  } else {
    // F24: with the document list closed, the hint opens it.
    nextStep = (
      <p className="app-text-muted text-sm">
        {t("sources.index.intro")}
        {sidebar === null || sidebar.isOpen ? null : (
          <> <AppButton variant="secondary" size="xs" onClick={sidebar.open}>{t("sources.index.openList")}</AppButton></>
        )}
      </p>
    );
  }

  return (
    <div className="max-w-2xl space-y-4 p-4">
      <h2 className="app-title text-xl font-bold">{t("sections.sources")}</h2>
      {nextStep}
      {/* The way to connect this PC, where a first visit lands, until one of theirs is connected (TR-A1-05). */}
      <ConnectorSetupButton inline />
      {projects.selectedProjectId === null ? null : (
        <SourceConnections key={projects.selectedProjectId} projectId={projects.selectedProjectId} mayUpdate={mayImport}
          corpusNames={new Map(corpora?.status === "loaded" ? corpora.value.map((corpus) => [corpus.corpusId, corpus.name]) : [])} />
      )}
      {projects.selectedProjectId === null ? null : (
        <>
          <ImportProposalList projectId={projects.selectedProjectId} state="open" />
          <ProjectBatches projectId={projects.selectedProjectId} />
        </>
      )}
    </div>
  );
}
